import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { ScrollText, UserCog, Eye, UserPlus, UserMinus, ShieldAlert } from "lucide-react";

const ACTION_META = {
  "impersonate.start": { label: "Started view-as",   icon: Eye,         tone: "bg-amber-100 text-amber-800 border-amber-200" },
  "impersonate.stop":  { label: "Ended view-as",     icon: Eye,         tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
  "user.update":       { label: "Updated user",      icon: UserCog,     tone: "bg-blue-100 text-blue-800 border-blue-200" },
  "user.delete":       { label: "Deleted user",      icon: UserMinus,   tone: "bg-red-100 text-red-800 border-red-200" },
  "user.bulk_create":  { label: "Created via CSV",   icon: UserPlus,    tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  "user.bulk_update":  { label: "Updated via CSV",   icon: UserCog,     tone: "bg-blue-100 text-blue-800 border-blue-200" },
};

function fmtValue(v) {
  if (v === null || v === undefined) return "—";
  if (Array.isArray(v)) return v.join(", ") || "—";
  return String(v);
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

export default function AuditLog() {
  const [entries, setEntries] = useState([]);
  const [action, setAction] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (action !== "all") params.action = action;
      const { data } = await api.get("/audit", { params });
      setEntries(data.entries || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [action]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-8" data-testid="audit-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500 flex items-center gap-2">
            <ShieldAlert className="h-3.5 w-3.5" /> Administration · ESUSD
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">Master Audit Log</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every impersonation session and user / role / site change is recorded here — who did what, and when.
          </p>
        </div>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-56 h-11" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            <SelectItem value="impersonate.start">Impersonation started</SelectItem>
            <SelectItem value="impersonate.stop">Impersonation ended</SelectItem>
            <SelectItem value="user.update">User updates</SelectItem>
            <SelectItem value="user.delete">User deletions</SelectItem>
            <SelectItem value="user.bulk_create">Bulk-import created</SelectItem>
            <SelectItem value="user.bulk_update">Bulk-import updated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md mt-6 overflow-x-auto floating-x-scroll">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">When</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Action</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Actor</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Target</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.05em] text-zinc-500 font-semibold">Changes / Note</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-16">Loading audit log…</TableCell></TableRow>
            )}
            {!loading && entries.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-zinc-500 py-16"><ScrollText className="h-6 w-6 mx-auto mb-2 text-zinc-400" />No events recorded yet.</TableCell></TableRow>
            )}
            {entries.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, icon: ScrollText, tone: "bg-zinc-100 text-zinc-700 border-zinc-200" };
              const Icon = meta.icon;
              return (
                <TableRow key={e.id} className="border-b border-zinc-100 align-top" data-testid={`audit-row-${e.id}`}>
                  <TableCell className="text-xs text-zinc-500 whitespace-nowrap font-mono">{formatDate(e.created_at)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.tone}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-zinc-800">{e.actor_name || "—"}</div>
                    <div className="text-xs text-zinc-500 truncate">{e.actor_email}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-zinc-800">{e.target_name || "—"}</div>
                    <div className="text-xs text-zinc-500 truncate">{e.target_email}</div>
                  </TableCell>
                  <TableCell>
                    {e.changes && Object.keys(e.changes).length > 0 ? (
                      <ul className="space-y-0.5 text-xs">
                        {Object.entries(e.changes).map(([field, delta]) => (
                          <li key={field} className="text-zinc-700">
                            <span className="font-mono text-zinc-500">{field}:</span>{" "}
                            <span className="text-red-600 line-through">{fmtValue(delta.from)}</span>{" "}
                            <span className="text-zinc-400">→</span>{" "}
                            <span className="text-emerald-700">{fmtValue(delta.to)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-xs text-zinc-500">{e.note || "—"}</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
